import React, { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Eye, X, Truck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getAgencyConnect, updateAgency } from "../../../api/auth";
import Loading from "../../ui/Loading";

const Livraison_list = () => {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [defaultLoadingId, setDefaultLoadingId] = useState(null);

  const navigate = useNavigate();

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });

    setTimeout(() => {
      setToast(null);
    }, 3000);
  }, []);

  const loadConnections = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getAgencyConnect();
      setData(res || []);
    } catch (err) {
      console.error(err);
      showToast("Error loading connections.", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  const filtered = data.filter((item) =>
    item.connection_name?.toLowerCase().includes(search.toLowerCase())
  );

  const updateDefault = async (agency) => {
    try {
      setDefaultLoadingId(agency.id);

      await updateAgency(
        {
          is_default: !agency.is_default,
        },
        agency.id
      );

      const res = await getAgencyConnect();
      setData(res || []);

      showToast(
        !agency.is_default
          ? `${agency.connection_name} is now the default connection.`
          : `${agency.connection_name} is no longer the default connection.`,
        "success"
      );
    } catch (err) {
      console.error(err);
      showToast("Error updating default connection. Please try again!", "error");
    } finally {
      setDefaultLoadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <Loading
          fullScreen
          text="Loading delivery connections..."
          variant="dots"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 dark:bg-slate-950">
      <div className="mx-auto max-w-7xl space-y-8">
        <div>
          <h1 className="text-3xl font-semibold text-gray-800 dark:text-slate-100">
            Delivery Integrations
          </h1>

          <p className="mt-1 text-gray-500 dark:text-slate-500">
            Manage connected delivery companies
          </p>
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="relative w-full max-w-md">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500"
            />

            <input
              type="text"
              placeholder="Search connection..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-10 pr-4 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
            />
          </div>

          <button
            onClick={() => navigate("/delivery/add-company")}
            className="rounded-xl bg-indigo-600 px-5 py-2.5 text-white shadow-sm transition hover:bg-indigo-700"
          >
            + Add Integration
          </button>
        </div>

        {/* <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left">Connection</th>
                  <th className="px-6 py-4 text-left">Account</th>
                  <th className="px-6 py-4 text-left">API Key</th>
                  <th className="px-6 py-4 text-left">Enabled</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((item, index) => (
                  <motion.tr
                    key={item.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.04 }}
                    className="border-b hover:bg-gray-50"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 font-medium">
                        <Truck size={16} className="text-indigo-600" />
                        <span>{item.connection_name}</span>

                        {item.is_default && (
                          <span className="rounded bg-indigo-100 px-2 py-0.5 text-xs text-indigo-600">
                            Default
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4 font-mono text-gray-600">
                      {item.external_account_id || item.client_id || "-"}
                    </td>

                    <td className="px-6 py-4 font-mono text-gray-600">
                      {item.api_key || item.access_token || "-"}
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => handleToggleActive(item)}
                          disabled={switchLoadingId === item.id}
                          className={`relative inline-flex h-7 w-12 items-center rounded-full transition ${
                            item.is_active ? "bg-green-500" : "bg-gray-300"
                          } ${
                            switchLoadingId === item.id
                              ? "cursor-not-allowed opacity-70"
                              : "cursor-pointer"
                          }`}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                              item.is_active ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                        </button>

                        <span
                          className={`text-xs font-medium ${
                            item.is_active ? "text-green-600" : "text-gray-500"
                          }`}
                        >
                          {switchLoadingId === item.id ? (
                            <span className="inline-flex items-center gap-1">
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              Updating...
                            </span>
                          ) : item.is_active ? (
                            "Enabled"
                          ) : (
                            "Disabled"
                          )}
                        </span>
                      </div>
                    </td>

                    <td className="space-x-3 px-6 py-4 text-right">
                      <button
                        onClick={() => setSelected(item)}
                        className="text-indigo-600 transition hover:text-indigo-800"
                        title="View details"
                      >
                        <Eye size={18} />
                      </button>

                      <button
                        onClick={() => updateDefault(item)}
                        disabled={defaultLoadingId === item.id}
                        className={`rounded-lg px-3 py-1.5 text-sm font-medium text-white transition ${
                          item.is_default
                            ? "bg-gray-500 hover:bg-gray-600"
                            : "bg-indigo-600 hover:bg-indigo-700"
                        } ${
                          defaultLoadingId === item.id
                            ? "cursor-not-allowed opacity-70"
                            : ""
                        }`}
                      >
                        {defaultLoadingId === item.id
                          ? "Saving..."
                          : item.is_default
                          ? "Remove default"
                          : "Set default"}
                      </button>
                    </td>
                  </motion.tr>
                ))}

                {!filtered.length && (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-10 text-center text-sm text-gray-500"
                    >
                      No connections found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div> */}

<div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
  <div className="overflow-x-hidden">
    <table className="w-full table-fixed text-sm">
      <thead className="border-b bg-gray-50 dark:bg-slate-800 dark:border-slate-800">
        <tr>
          <th className="px-6 py-4 text-left w-[30%] dark:text-slate-300">Connection</th>
          <th className="px-6 py-4 text-left w-[25%] dark:text-slate-300">Account</th>
          <th className="px-6 py-4 text-left w-[30%] dark:text-slate-300">API Key</th>
          <th className="px-6 py-4 text-right w-[15%] dark:text-slate-300">Actions</th>
        </tr>
      </thead>

      <tbody>
        {filtered.map((item, index) => (
          <motion.tr
            key={item.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.04 }}
            className="border-b hover:bg-gray-50 dark:border-slate-800 dark:hover:bg-slate-800"
          >
            {/* Connection */}
            <td className="px-6 py-4">
              <div className="flex items-center gap-2 font-medium truncate dark:text-slate-100">
                <Truck size={16} className="text-indigo-600 shrink-0 dark:text-indigo-400" />
                <span className="truncate">{item.connection_name}</span>

                {item.is_default && (
                  <span className="shrink-0 rounded bg-indigo-100 px-2 py-0.5 text-xs text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400">
                    Default
                  </span>
                )}
              </div>
            </td>

            {/* Account */}
            <td className="px-6 py-4 font-mono text-gray-600 max-w-[150px] truncate dark:text-slate-400">
              <span title={item.external_account_id || item.client_id}>
                {item.external_account_id || item.client_id || "-"}
              </span>
            </td>

            {/* API Key */}
            <td className="px-6 py-4 font-mono text-gray-600 max-w-[200px] truncate dark:text-slate-400">
              <span title={item.api_key || item.access_token}>
                {item.api_key || item.access_token || "-"}
              </span>
            </td>

            {/* Enabled */}
            {/* <td className="px-6 py-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => handleToggleActive(item)}
                  disabled={switchLoadingId === item.id}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full transition ${
                    item.is_active ? "bg-green-500" : "bg-gray-300"
                  } ${
                    switchLoadingId === item.id
                      ? "cursor-not-allowed opacity-70"
                      : "cursor-pointer"
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                      item.is_active ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>

                <span
                  className={`text-xs font-medium ${
                    item.is_active ? "text-green-600" : "text-gray-500"
                  }`}
                >
                  {switchLoadingId === item.id ? (
                    <span className="inline-flex items-center gap-1">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Updating...
                    </span>
                  ) : item.is_active ? (
                    "Enabled"
                  ) : (
                    "Disabled"
                  )}
                </span>
              </div>
            </td> */}

            {/* Actions */}
            <td className="space-x-3 px-6 py-4 text-right">
              <button
                onClick={() => setSelected(item)}
                className="text-indigo-600 transition hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                title="View details"
              >
                <Eye size={18} />
              </button>

              <button
                onClick={() => updateDefault(item)}
                disabled={defaultLoadingId === item.id}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium text-white transition ${
                  item.is_default
                    ? "bg-gray-500 hover:bg-gray-600"
                    : "bg-indigo-600 hover:bg-indigo-700"
                } ${
                  defaultLoadingId === item.id
                    ? "cursor-not-allowed opacity-70"
                    : ""
                }`}
              >
                {defaultLoadingId === item.id
                  ? "Saving..."
                  : item.is_default
                  ? "Remove default"
                  : "Set default"}
              </button>
            </td>
          </motion.tr>
        ))}

        {!filtered.length && (
          <tr>
            <td
              colSpan={5}
              className="px-6 py-10 text-center text-sm text-gray-500 dark:text-slate-500"
            >
              No connections found.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
</div>

        <AnimatePresence>
          {selected && (
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="relative w-full max-w-lg rounded-2xl bg-white p-8 shadow-2xl dark:bg-slate-900"
                initial={{ y: 40 }}
                animate={{ y: 0 }}
                exit={{ y: 40 }}
              >
                <button
                  onClick={() => setSelected(null)}
                  className="absolute right-5 top-5 text-gray-400 transition hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300"
                >
                  <X size={18} />
                </button>

                <h2 className="mb-6 text-xl font-semibold dark:text-slate-100">
                  Connection Details
                </h2>

                <div className="space-y-4 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-slate-500">Connection</span>
                    <p className="font-medium dark:text-slate-100">{selected.connection_name}</p>
                  </div>

                  <div>
                    <span className="text-gray-500 dark:text-slate-500">Account ID</span>
                    <p className="font-mono dark:text-slate-300">
                      {selected.external_account_id || selected.client_id || "-"}
                    </p>
                  </div>

                  <div>
                    <span className="text-gray-500 dark:text-slate-500">API Key</span>
                    <p className="font-mono dark:text-slate-300">
                      {selected.api_key || selected.access_token || "-"}
                    </p>
                  </div>

                  {/* <div>
                    <span className="text-gray-500">Status</span>
                    <p className="font-medium">
                      {selected.is_active ? "Enabled" : "Disabled"}
                    </p>
                  </div> */}

                  <div>
                    <span className="text-gray-500 dark:text-slate-500">Created</span>
                    <p className="dark:text-slate-300">{new Date(selected.created_at).toLocaleString()}</p>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => setSelected(null)}
                    className="rounded-lg bg-gray-100 px-4 py-2 transition hover:bg-gray-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                  >
                    Close
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed right-5 top-5 rounded-xl px-5 py-3 font-medium text-white shadow-lg ${
              toast.type === "success" ? "bg-green-500" : "bg-red-500"
            }`}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Livraison_list;
